import { useState, useEffect, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { FileDown, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useProducerSettings } from "@/hooks/useProducerSettings";
import type { DayStageOptions } from "@/lib/stageExportOptions";

interface CrewMember {
  id: string;
  name: string;
  function: string;
  color: string | null;
  event_date: string;
}

interface ScheduleExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  briefingId: string;
  festivalName: string;
  eventDays: string[];
  // Crew rows are per-day (one row per person per day). The export groups them
  // by day so the producer can pick which crew of which day to export.
  crewMembers: CrewMember[];
  // Stages present per day (for the producer "which stages" filter). Optional:
  // callers without the full lineup loaded omit it and the stage filter hides.
  stagesByDay?: DayStageOptions[];
  // The day the producer is currently viewing — the export defaults its
  // selection to this day so it matches what's on screen. Falls back to all
  // days when omitted.
  currentDay?: string;
}

type ExportType = "producer" | "crew";
type ViewStyle = "list" | "timeline";
type SortBy = "stage" | "crew";

export function ScheduleExportModal({
  open,
  onOpenChange,
  briefingId,
  festivalName,
  eventDays,
  crewMembers,
  stagesByDay = [],
  currentDay,
}: ScheduleExportModalProps) {
  const { settings: producerSettings } = useProducerSettings();

  const [exportType, setExportType] = useState<ExportType>("producer");
  const [viewStyle, setViewStyle] = useState<ViewStyle>("list");
  const [sortBy, setSortBy] = useState<SortBy>("stage");
  const [backgroundStyle, setBackgroundStyle] = useState<"colored" | "white">("colored");
  const [selectedCrewIds, setSelectedCrewIds] = useState<string[]>([]);
  const [selectedStageKeys, setSelectedStageKeys] = useState<string[]>([]);
  const [combinePdfs, setCombinePdfs] = useState(false);
  const [includeStageColors, setIncludeStageColors] = useState(true);
  const [includeAssignmentColors, setIncludeAssignmentColors] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  const isMultiDay = eventDays.length > 1;

  // Crew grouped per day, in event-day order, dropping days with no crew.
  const crewByDay = useMemo(
    () =>
      eventDays
        .map((day) => ({
          day,
          members: crewMembers.filter((m) => m.event_date === day),
        }))
        .filter((g) => g.members.length > 0),
    [eventDays, crewMembers],
  );

  const allStageKeys = useMemo(
    () => stagesByDay.flatMap((d) => d.options.map((o) => o.key)),
    [stagesByDay],
  );

  // Initialize selections ONCE each time the modal opens — never on later
  // re-renders. A previous version re-selected crew whenever the selection
  // emptied, which made "Deselect all" flicker and refill. Default to the day
  // currently being viewed so the export matches the on-screen schedule.
  const initialized = useRef(false);
  useEffect(() => {
    if (!open) {
      initialized.current = false;
      return;
    }
    if (initialized.current) return;
    initialized.current = true;

    setViewStyle((producerSettings?.pdf_default_view as ViewStyle) || "list");
    setSortBy("stage");

    const dayStages = stagesByDay.find((d) => d.day === currentDay)?.options;
    setSelectedStageKeys((dayStages ?? stagesByDay.flatMap((d) => d.options)).map((o) => o.key));

    const dayCrew = crewByDay.find((g) => g.day === currentDay)?.members;
    setSelectedCrewIds((dayCrew ?? crewMembers).map((m) => m.id));
  }, [open, producerSettings, stagesByDay, crewByDay, crewMembers, currentDay]);

  const handleCrewToggle = (crewId: string) => {
    setSelectedCrewIds((prev) =>
      prev.includes(crewId) ? prev.filter((id) => id !== crewId) : [...prev, crewId],
    );
  };

  const handleStageToggle = (key: string) => {
    setSelectedStageKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleExport = async () => {
    // Which days get exported. Producer: the days that have at least one
    // selected stage (selecting per day IS the date selection — there's no
    // separate date picker). Crew: ALL event days, because the backend scopes
    // by crew_member_id and builds one file per (person, day they actually
    // film), so it must see every day's assignments.
    const producerDates =
      stagesByDay.length === 0
        ? eventDays
        : stagesByDay
            .filter((d) => d.options.some((o) => selectedStageKeys.includes(o.key)))
            .map((d) => d.day);
    const exportDates = exportType === "producer" ? producerDates : eventDays;

    if (exportType === "producer" && stagesByDay.length > 0 && selectedStageKeys.length === 0) {
      toast.error("Please select at least one stage");
      return;
    }
    if (exportType === "crew" && selectedCrewIds.length === 0) {
      toast.error("Please select at least one crew member");
      return;
    }
    if (exportDates.length === 0) {
      toast.error("Please select at least one day");
      return;
    }

    setIsGenerating(true);

    try {
      // Send the stage filter only when it actually narrows the set — and only
      // for the producer view (crew view always shows all stages for the
      // selected crew, so there's no stage picker there).
      const stageFilter =
        exportType === "producer" &&
        stagesByDay.length > 0 &&
        selectedStageKeys.length < allStageKeys.length
          ? selectedStageKeys
          : undefined;

      const response = await supabase.functions.invoke("export-schedule-pdf", {
        body: {
          briefingId,
          exportType,
          viewStyle: exportType === "producer" ? viewStyle : "list",
          sortBy: exportType === "producer" ? sortBy : "stage",
          selectedDates: exportDates,
          selectedCrewMemberIds: exportType === "crew" ? selectedCrewIds : undefined,
          selectedStageKeys: stageFilter,
          combinePdfs,
          options: {
            includeStageColors,
            includeAssignmentColors,
            expandedMode: false,
            backgroundStyle,
          },
        },
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to generate PDF");
      }

      const blob = response.data;

      // Determine filename based on producer settings
      let filename: string;
      const dateStr =
        exportDates.length === 1 ? format(new Date(exportDates[0]), "yyyy-MM-dd") : "multi";
      const filenameStructure = producerSettings?.pdf_filename_structure || "festival_date";

      if (exportType === "crew" && selectedCrewIds.length === 1) {
        const crewMember = crewMembers.find((m) => m.id === selectedCrewIds[0]);
        if (filenameStructure === "crew_name") {
          filename = `${crewMember?.name || "Crew"}_assignments.pdf`;
        } else {
          filename = `PROduce - ${festivalName} - ${crewMember?.name || "Crew"} Schedule.pdf`;
        }
      } else if (exportType === "crew" && !combinePdfs && selectedCrewIds.length > 1) {
        filename = `PROduce - ${festivalName} - Crew Schedules.zip`;
      } else {
        // Producer view or combined crew
        switch (filenameStructure) {
          case "festival_date":
            filename = `${festivalName}_${dateStr}_schedule.pdf`;
            break;
          case "festival_day_view":
            filename = `${festivalName}_${dateStr}_${viewStyle}.pdf`;
            break;
          default:
            filename = `PROduce - ${festivalName} - Schedule.pdf`;
        }
      }

      // Create download link
      const url = URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("PDF exported successfully");
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to export PDF");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="h-5 w-5 text-primary" />
            Export Schedule PDF
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Export Type */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Export type</Label>
            <RadioGroup
              value={exportType}
              onValueChange={(value) => setExportType(value as ExportType)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="producer" id="producer" />
                <Label htmlFor="producer" className="cursor-pointer">
                  Producer view
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="crew" id="crew" />
                <Label htmlFor="crew" className="cursor-pointer">
                  Crew member view
                </Label>
              </div>
            </RadioGroup>
          </div>

          <Separator />

          {/* Background Style */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Background style</Label>
            <RadioGroup
              value={backgroundStyle}
              onValueChange={(value) => setBackgroundStyle(value as "colored" | "white")}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="colored" id="colored" />
                <Label htmlFor="colored" className="cursor-pointer">
                  <span className="font-medium">Colored</span>
                  <span className="ml-1 text-xs text-muted-foreground">(PROduce branding)</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="white" id="white" />
                <Label htmlFor="white" className="cursor-pointer">
                  <span className="font-medium">White</span>
                  <span className="ml-1 text-xs text-muted-foreground">(print-friendly)</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <Separator />

          {/* View Style + Sort (Producer View only) */}
          {exportType === "producer" && (
            <>
              <div className="space-y-3">
                <Label className="text-sm font-medium">View style</Label>
                <RadioGroup
                  value={viewStyle}
                  onValueChange={(value) => setViewStyle(value as ViewStyle)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="list" id="list" />
                    <Label htmlFor="list" className="cursor-pointer">
                      List view
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="timeline" id="timeline" />
                    <Label htmlFor="timeline" className="cursor-pointer">
                      Timeline view
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Sort order — only the list view can be re-grouped; the timeline
                  is an inherent time-axis grid. */}
              {viewStyle === "list" && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Sort by</Label>
                  <RadioGroup
                    value={sortBy}
                    onValueChange={(value) => setSortBy(value as SortBy)}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="stage" id="sort-stage" />
                      <Label htmlFor="sort-stage" className="cursor-pointer">
                        Stage
                        <span className="ml-1 text-xs text-muted-foreground">(per stage)</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="crew" id="sort-crew" />
                      <Label htmlFor="sort-crew" className="cursor-pointer">
                        Crew
                        <span className="ml-1 text-xs text-muted-foreground">
                          (per crew member)
                        </span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}
              <Separator />
            </>
          )}

          {/* Stage filter (Producer View) — grouped per day so a multi-day
              festival doesn't show the same stage name several times without
              context. Hidden when there's nothing meaningful to pick. */}
          {exportType === "producer" && allStageKeys.length > 1 && (
            <>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Stages</Label>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedStageKeys(allStageKeys)}
                      className="text-xs"
                    >
                      Select all
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedStageKeys([])}
                      className="text-xs"
                    >
                      Deselect all
                    </Button>
                  </div>
                </div>
                <div className="max-h-48 space-y-3 overflow-y-auto rounded-md border border-border p-3">
                  {stagesByDay.map((group) => (
                    <div key={group.day} className="space-y-2">
                      {isMultiDay && (
                        <p className="text-xs font-semibold text-muted-foreground">
                          {format(new Date(group.day), "EEEE, MMMM d")}
                        </p>
                      )}
                      {group.options.map((stage) => (
                        <div key={stage.key} className="flex items-center gap-2 pl-1">
                          <Checkbox
                            id={`stage-${stage.key}`}
                            checked={selectedStageKeys.includes(stage.key)}
                            onCheckedChange={() => handleStageToggle(stage.key)}
                          />
                          <Label htmlFor={`stage-${stage.key}`} className="cursor-pointer text-sm">
                            {stage.label || "Unnamed stage"}
                          </Label>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Crew Member Selection (Crew View) — grouped per day. */}
          {exportType === "crew" && (
            <>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Select crew members</Label>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedCrewIds(crewMembers.map((m) => m.id))}
                      className="text-xs"
                    >
                      Select all
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedCrewIds([])}
                      className="text-xs"
                    >
                      Deselect all
                    </Button>
                  </div>
                </div>
                <div className="max-h-48 space-y-3 overflow-y-auto rounded-md border border-border p-3">
                  {crewByDay.map((group) => (
                    <div key={group.day} className="space-y-2">
                      {isMultiDay && (
                        <p className="text-xs font-semibold text-muted-foreground">
                          {format(new Date(group.day), "EEEE, MMMM d")}
                        </p>
                      )}
                      {group.members.map((member) => (
                        <div key={member.id} className="flex items-center gap-2 pl-1">
                          <Checkbox
                            id={`crew-${member.id}`}
                            checked={selectedCrewIds.includes(member.id)}
                            onCheckedChange={() => handleCrewToggle(member.id)}
                          />
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: member.color || "#888" }}
                          />
                          <Label htmlFor={`crew-${member.id}`} className="cursor-pointer text-sm">
                            {member.name}{" "}
                            <span className="text-muted-foreground">({member.function})</span>
                          </Label>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {selectedCrewIds.length > 1 && (
                  <div className="flex items-center justify-between rounded-md bg-muted/30 p-3">
                    <Label htmlFor="combine" className="cursor-pointer text-sm">
                      Combine all into one PDF
                    </Label>
                    <Switch id="combine" checked={combinePdfs} onCheckedChange={setCombinePdfs} />
                  </div>
                )}
              </div>
              <Separator />
            </>
          )}

          {/* Additional Options */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Additional options</Label>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="stageColors" className="cursor-pointer text-sm">
                  Include stage color indicators
                </Label>
                <Switch
                  id="stageColors"
                  checked={includeStageColors}
                  onCheckedChange={setIncludeStageColors}
                />
              </div>

              {exportType === "producer" && viewStyle === "timeline" && (
                <div className="flex items-center justify-between">
                  <Label htmlFor="assignmentColors" className="cursor-pointer text-sm">
                    Include assignment colors
                  </Label>
                  <Switch
                    id="assignmentColors"
                    checked={includeAssignmentColors}
                    onCheckedChange={setIncludeAssignmentColors}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isGenerating}>
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <FileDown className="mr-2 h-4 w-4" />
                Export PDF
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
